import { useState } from 'react';
import { DEFAULT_AVAILABILITY, type Availability } from '@acs/shared';
import { Button, Card, DateTile, Icon, IconButton, Input, Skeleton } from '@acs/ui';
import { useAvailability, useSaveAvailability } from '../settings/hooks.js';
import { useI18n } from '../../i18n/index.js';

/**
 * Business-wide closed dates (doc 10 §5.1) — holidays that close the shop for
 * everyone, staff schedules included (doc 09 D2). Edits the *empty-array*
 * entries of `availability.exceptions`; any non-empty (special short-day)
 * exceptions are preserved untouched (they have no dedicated UI yet, doc 10 §11).
 */
export function ClosedDatesCard({ businessId }: { businessId: string }) {
  const { t, tag } = useI18n();
  // Month abbreviations from the platform rather than a hard-coded list —
  // the tile has room for three letters in any of the five languages.
  const monthShort = new Intl.DateTimeFormat(tag, { month: 'short', timeZone: 'UTC' });
  const { data, isLoading } = useAvailability(businessId);
  const save = useSaveAvailability(businessId);
  const [newDate, setNewDate] = useState('');

  if (isLoading) {
    return (
      <Card title={t('closedDates.title')}>
        <Skeleton height={120} radius="var(--tc-radius-sm)" />
      </Card>
    );
  }

  const availability: Availability = data ?? DEFAULT_AVAILABILITY;
  const closedDates = Object.entries(availability.exceptions)
    .filter(([, ivs]) => ivs.length === 0)
    .map(([d]) => d)
    .sort();

  const persist = (exceptions: Availability['exceptions']) =>
    save.mutate({ ...availability, exceptions });

  const addDate = () => {
    if (!newDate || availability.exceptions[newDate]) return;
    persist({ ...availability.exceptions, [newDate]: [] });
    setNewDate('');
  };

  const removeDate = (d: string) => {
    const next = { ...availability.exceptions };
    delete next[d];
    persist(next);
  };

  return (
    <Card title={t('closedDates.title')}>
      <p className="field-hint" style={{ marginBottom: 12 }}>{t('closedDates.hint')}</p>

      {closedDates.length === 0 ? (
        <p className="muted">{t('closedDates.empty')}</p>
      ) : (
        <ul className="closed-dates">
          {closedDates.map((d) => (
            <li key={d}>
              <DateTile
                day={d.slice(8, 10)}
                month={monthShort.format(new Date(`${d}T12:00:00Z`))}
                tone="warning"
                label={d}
              />
              <span className="mono" style={{ flex: 1 }}>{d}</span>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label={t('closedDates.remove', { date: d })}
                icon={<Icon name="x" size={16} />}
                onClick={() => removeDate(d)}
              />
            </li>
          ))}
        </ul>
      )}

      <div className="row" style={{ marginTop: 16 }}>
        <Input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          aria-label={t('closedDates.add')}
          style={{ width: 'auto' }}
        />
        <Button
          variant="secondary"
          leadingIcon={<Icon name="plus" size={16} />}
          onClick={addDate}
          disabled={!newDate || save.isPending}
        >
          {t('closedDates.add')}
        </Button>
      </div>
    </Card>
  );
}
